import { Menu, MenuContent, MenuItem, MenuLabel } from "@/components/ui/menu.tsx";
import { useFeedbackCatalogClient } from "@domains/feedback/client/api/feedbackCatalogQuery.ts";
import { FEEDBACK_METRIC_IDS, FEEDBACK_SOURCES, type FeedbackSource } from "@domains/feedback/shared/feedbackTypes.ts";
import { FunnelIcon } from "@heroicons/react/20/solid";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { FilterChipButton } from "@packages/core-client/collection/FilterChip.tsx";
import type { FilterChipMenuItem } from "@packages/core-client/collection/FilterChipMenu.tsx";
import { ResetFiltersButton } from "@packages/core-client/collection/ResetFiltersButton.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import type { Key } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { AnalyticsFilterDimensions, type AnalyticsFilterDimension, useAnalyticsTracking } from "../hooks/useAnalyticsTracking.ts";
import { FEEDBACK_FILTER_SENTIMENTS, useFeedbackFilterState } from "../hooks/useFeedbackFilters.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";
import { FeedbackFilterChip } from "./FeedbackFilterChip.tsx";

const FEEDBACK_FILTER_DIMENSIONS = ["sentiment", "labelId", "metricId", "feedbackType"] as const;
type FeedbackFilterDimension = (typeof FEEDBACK_FILTER_DIMENSIONS)[number];

const FEEDBACK_TYPE_OPTION_ORDER = ["Officevibe", "Custom", "Onboarding", "NonSurvey", "Smart"] as const satisfies ReadonlyArray<FeedbackSource>;

const TRACKED_DIMENSION: Record<FeedbackFilterDimension, AnalyticsFilterDimension> = {
    sentiment: AnalyticsFilterDimensions.Sentiment,
    labelId: AnalyticsFilterDimensions.Label,
    metricId: AnalyticsFilterDimensions.Metric,
    feedbackType: AnalyticsFilterDimensions.FeedbackType
};

const NO_DIMENSIONS: ReadonlySet<FeedbackFilterDimension> = new Set();

function toFilterPatch(dimension: FeedbackFilterDimension, id: string) {
    switch (dimension) {
        case "feedbackType": {
            const feedbackType = FEEDBACK_SOURCES.find(source => source === id);

            return feedbackType ? { feedbackType } : null;
        }
        case "sentiment": {
            const sentiment = FEEDBACK_FILTER_SENTIMENTS.find(value => value === id);

            return sentiment ? { sentiment } : null;
        }
        case "metricId": {
            const metricId = FEEDBACK_METRIC_IDS.find(value => value === id);

            return metricId ? { metricId } : null;
        }
        case "labelId":
            return { labelId: id };
    }
}

function withSelectedFallback(options: FilterChipMenuItem[] | undefined, isError: boolean, selectedId: string | null, fallbackLabel: string): ReadonlyArray<FilterChipMenuItem> {
    if (!options) {
        return isError && selectedId ? [{ id: selectedId, label: fallbackLabel }] : [];
    }

    if (!selectedId || options.some(option => option.id === selectedId)) {
        return options;
    }

    return [...options, { id: selectedId, label: fallbackLabel }];
}

function findDimension(key: Key) {
    return FEEDBACK_FILTER_DIMENSIONS.find(dimension => dimension === key);
}

export function FeedbackFilterBar() {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("FeedbackFilterBar", { i18n: i18nextInstance });
    const client = useFeedbackCatalogClient();
    const { trackFilterApplied, trackFiltersCleared } = useAnalyticsTracking();

    const [filters, setFilters] = useFeedbackFilterState();
    const [revealedDimensions, setRevealedDimensions] = useState<ReadonlySet<FeedbackFilterDimension>>(NO_DIMENSIONS);
    const [openDimension, setOpenDimension] = useState<FeedbackFilterDimension | null>(null);
    const addFiltersRef = useRef<HTMLButtonElement>(null);

    const visibleDimensions = useMemo(() => FEEDBACK_FILTER_DIMENSIONS.filter(dimension => !!filters[dimension] || revealedDimensions.has(dimension)), [filters, revealedDimensions]);
    const hasActiveFilters = FEEDBACK_FILTER_DIMENSIONS.some(dimension => !!filters[dimension]);

    const language = i18nextInstance.language;
    const metricsOptions = client.getMetrics.queryOptions();

    const labelsQuery = useQuery(createQueryOptions(client.getLabels.queryOptions(), { throwOnError: false }));
    const metricsQuery = useQuery(createQueryOptions({ ...metricsOptions, queryKey: [...metricsOptions.queryKey, { language }] }, { throwOnError: false }));

    const addFilterItems = useMemo(() => FEEDBACK_FILTER_DIMENSIONS.map(dimension => ({ id: dimension, label: t(`dimensions.${dimension}`) })), [t]);

    const optionsByDimension = useMemo<Record<FeedbackFilterDimension, ReadonlyArray<FilterChipMenuItem>>>(
        () => ({
            sentiment: FEEDBACK_FILTER_SENTIMENTS.map(sentiment => ({ id: sentiment, label: t(`sentiments.${sentiment}`) })),
            labelId: withSelectedFallback(
                labelsQuery.data?.items.map(label => ({ id: label.id, label: label.text })),
                labelsQuery.isError,
                filters.labelId,
                t("unavailable.labelId")
            ),
            metricId: withSelectedFallback(
                metricsQuery.data?.items.map(metric => ({ id: metric.id, label: metric.text })),
                metricsQuery.isError,
                filters.metricId,
                t("unavailable.metricId")
            ),
            feedbackType: FEEDBACK_TYPE_OPTION_ORDER.map(source => ({ id: source, label: t(`types.${source}`) }))
        }),
        [t, labelsQuery.data, labelsQuery.isError, metricsQuery.data, metricsQuery.isError, filters.labelId, filters.metricId]
    );

    const revealDimension = useCallback((dimension: FeedbackFilterDimension) => setRevealedDimensions(previous => new Set(previous).add(dimension)), []);

    const handleAddFilter = useCallback(
        (key: Key) => {
            const dimension = findDimension(key);

            if (dimension) {
                revealDimension(dimension);
                setOpenDimension(dimension);
            }
        },
        [revealDimension]
    );

    const handleSelect = useCallback(
        (dimension: FeedbackFilterDimension, id: string) => {
            const patch = toFilterPatch(dimension, id);

            if (patch) {
                void setFilters(patch);
                trackFilterApplied(TRACKED_DIMENSION[dimension]);
            }

            setOpenDimension(null);
        },
        [setFilters, trackFilterApplied]
    );

    const handleClear = useCallback(
        (dimension: FeedbackFilterDimension) => {
            revealDimension(dimension);
            void setFilters({ [dimension]: null });
        },
        [revealDimension, setFilters]
    );

    const handleDismiss = useCallback(
        (dimension: FeedbackFilterDimension) => {
            addFiltersRef.current?.focus();
            setRevealedDimensions(previous => new Set([...previous].filter(value => value !== dimension)));
            setOpenDimension(previous => (previous === dimension ? null : previous));
            void setFilters({ [dimension]: null });
        },
        [setFilters]
    );

    const handleOpenChange = useCallback((dimension: FeedbackFilterDimension, isOpen: boolean) => setOpenDimension(isOpen ? dimension : null), []);

    const handleClearAll = useCallback(() => {
        addFiltersRef.current?.focus();
        setRevealedDimensions(NO_DIMENSIONS);
        setOpenDimension(null);
        void setFilters(null);
        trackFiltersCleared();
    }, [setFilters, trackFiltersCleared]);

    const errorMessageByDimension: Partial<Record<FeedbackFilterDimension, string>> = {
        labelId: labelsQuery.isError ? t("errors.labelId") : undefined,
        metricId: metricsQuery.isError ? t("errors.metricId") : undefined
    };
    const isPendingByDimension: Partial<Record<FeedbackFilterDimension, boolean>> = {
        labelId: labelsQuery.isPending,
        metricId: metricsQuery.isPending
    };

    return (
        <>
            <Menu>
                <FilterChipButton ref={addFiltersRef}>
                    <FunnelIcon aria-hidden="true" className="size-4" />
                    {t("addFilters")}
                </FilterChipButton>
                <MenuContent placement="bottom start" items={addFilterItems} disabledKeys={visibleDimensions} onAction={handleAddFilter}>
                    {item => (
                        <MenuItem id={item.id} textValue={item.label}>
                            <MenuLabel>{item.label}</MenuLabel>
                        </MenuItem>
                    )}
                </MenuContent>
            </Menu>

            {visibleDimensions.map(dimension => (
                <FeedbackDimensionChip
                    key={dimension}
                    dimension={dimension}
                    dimensionLabel={t(`dimensions.${dimension}`)}
                    options={optionsByDimension[dimension]}
                    selectedId={filters[dimension]}
                    isPending={isPendingByDimension[dimension]}
                    errorMessage={errorMessageByDimension[dimension]}
                    isOpen={openDimension === dimension}
                    onOpenChange={handleOpenChange}
                    onSelect={handleSelect}
                    onClear={handleClear}
                    onDismiss={handleDismiss}
                />
            ))}

            <ResetFiltersButton isActive={hasActiveFilters} onReset={handleClearAll} label={t("clearAllFilters")} />
        </>
    );
}

interface FeedbackDimensionChipProps {
    dimension: FeedbackFilterDimension;
    dimensionLabel: string;
    options: ReadonlyArray<FilterChipMenuItem>;
    selectedId: string | null;
    isPending?: boolean;
    errorMessage?: string;
    isOpen: boolean;
    onOpenChange: (dimension: FeedbackFilterDimension, isOpen: boolean) => void;
    onSelect: (dimension: FeedbackFilterDimension, id: string) => void;
    onClear: (dimension: FeedbackFilterDimension) => void;
    onDismiss: (dimension: FeedbackFilterDimension) => void;
}

function FeedbackDimensionChip({ dimension, onOpenChange, onSelect, onClear, onDismiss, ...props }: FeedbackDimensionChipProps) {
    const handleOpenChange = useCallback((isOpen: boolean) => onOpenChange(dimension, isOpen), [dimension, onOpenChange]);
    const handleSelect = useCallback((id: string) => onSelect(dimension, id), [dimension, onSelect]);
    const handleClear = useCallback(() => onClear(dimension), [dimension, onClear]);
    const handleDismiss = useCallback(() => onDismiss(dimension), [dimension, onDismiss]);

    return <FeedbackFilterChip {...props} onOpenChange={handleOpenChange} onSelect={handleSelect} onClear={handleClear} onDismiss={handleDismiss} />;
}
